// Generic function registry — remembers parameter types by name
class FunctionManager1<
  BaseParam1 extends object,
  Result>
{
  private registry = new Map<string, (arg1: BaseParam1) => Result>();

  // Register a function and return a new typed manager
  register<
    T1 extends BaseParam1,
  >(
    name: string,
    fn: (arg1: T1) => Result
  ): this {
    if (this.registry.has(name)) {
      throw new Error(`Function "${name}" is already registered`);
    }
    this.registry.set(name, fn as (a: BaseParam1) => Result);
    return this;
  }

  // Get a function with its exact parameter type
  get(name: string): (arg1: BaseParam1) => Result {
    const fn = this.registry.get(name);
    if (!fn) throw new Error(`Function "${name}" is not registered`);
    return fn;
  }
}

class FunctionManager2<
    BaseParam1 extends object,
    BaseParam2 extends object,
    Result,
> {
  private registry = new Map<string, (arg1: BaseParam1, arg2: BaseParam2) => Result>();

  // Register a function and return a new typed manager
  register<
    T1 extends BaseParam1,
    T2 extends BaseParam2
  >(
    name: string,
    fn: (arg1: T1, arg2: T2) => Result
  ): this {
    if (this.registry.has(name)) {
      throw new Error(`Function "${name}" is already registered`);
    }
    this.registry.set(name, fn as (a: BaseParam1, b: BaseParam2) => Result);
    return this;
  }

  // Get a function with its exact parameter type
  get(name: string): (arg1: BaseParam1, arg2: BaseParam2) => Result {
    const fn = this.registry.get(name);
    if (!fn) throw new Error(`Function "${name}" is not registered`);
    return fn;
  }
}

export const createFunctionManager1 = <BaseParam extends object, Result>() => new FunctionManager1<BaseParam, Result>();
export const createFunctionManager2 = <BaseParam1 extends object, BaseParam2 extends object, Result>() => new FunctionManager2<BaseParam1, BaseParam2, Result>();